import React, { useEffect, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ResponsiveContainer, ComposedChart, Line, Bar, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine
} from 'recharts';
import './HR.css';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const INR = n => '₹' + Number(n||0).toLocaleString('en-IN', { maximumFractionDigits:0 });

// Simple linear regression
function linearRegression(values) {
  const n = values.length;
  if (n < 2) return { slope: 0, intercept: values[0] || 0 };
  const xs = values.map((_,i) => i);
  const meanX = xs.reduce((a,b)=>a+b,0) / n;
  const meanY = values.reduce((a,b)=>a+b,0) / n;
  const num = xs.reduce((s,x,i) => s + (x-meanX)*(values[i]-meanY), 0);
  const den = xs.reduce((s,x) => s + (x-meanX)**2, 0);
  const slope = den ? num/den : 0;
  const intercept = meanY - slope * meanX;
  return { slope, intercept };
}

function forecast(values, futureN) {
  const { slope, intercept } = linearRegression(values);
  return Array.from({length: futureN}, (_,i) => Math.max(0, intercept + slope * (values.length + i)));
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'#fff', border:'1.5px solid #e2e8f0', borderRadius:10, padding:'10px 14px', fontSize:13, boxShadow:'0 4px 16px #0001' }}>
      <div style={{ fontWeight:700, marginBottom:6, color:'#0f172a' }}>{label}</div>
      {payload.map((p,i) => p.value != null && (
        <div key={i} style={{ color:p.color, marginBottom:2 }}>{p.name}: {INR(p.value)}</div>
      ))}
    </div>
  );
};

export default function RevenueForecast() {
  const [invoices,  setInvoices]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [forecastN, setForecastN] = useState(6);

  useEffect(() => {
    api.get('/invoices', { params: { limit: 500 } })
      .then(r => setInvoices(r.data.data || r.data.invoices || []))
      .catch(() => toast.error('Failed to load invoice data'))
      .finally(() => setLoading(false));
  }, []);

  // Build monthly revenue from paid invoices (last 12 months)
  const now = new Date();
  const monthlyRevenue = Array.from({length: 12}, (_,i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    const total = invoices
      .filter(inv => {
        if (!['paid','partial'].includes(inv.status)) return false;
        const pd = inv.paidOn || inv.updatedAt || inv.issueDate;
        return pd && pd.slice(0,7) === key;
      })
      .reduce((s,inv) => s + parseFloat(inv.amountInINR || inv.total || 0), 0);
    return { month: d, label: `${MONTHS[d.getMonth()]} ${d.getFullYear().toString().slice(2)}`, revenue: total };
  });

  const revenueValues  = monthlyRevenue.map(m => m.revenue);
  const forecastValues = forecast(revenueValues, forecastN);

  // Future month labels
  const futureLabels = Array.from({length: forecastN}, (_,i) => {
    const d = new Date(now.getFullYear(), now.getMonth() + 1 + i, 1);
    return `${MONTHS[d.getMonth()]} ${d.getFullYear().toString().slice(2)}`;
  });

  const allLabels = [...monthlyRevenue.map(m=>m.label), ...futureLabels];
  const allActual = [...revenueValues, ...Array(forecastN).fill(null)];
  const allForecast = [...Array(revenueValues.length).fill(null).map((_,i) => i===revenueValues.length-1 ? revenueValues[revenueValues.length-1] : null), ...forecastValues];
  // Fill last actual into forecast start for continuity
  allForecast[revenueValues.length - 1] = revenueValues[revenueValues.length - 1];

  const { slope } = linearRegression(revenueValues);
  const avgMonthly = revenueValues.reduce((a,b)=>a+b,0) / (revenueValues.filter(v=>v>0).length || 1);
  const nextMonth  = forecastValues[0] || 0;
  const nextQuarter = forecastValues.slice(0,3).reduce((a,b)=>a+b,0);
  const nextYear   = forecastValues.length >= 12 ? forecastValues.slice(0,12).reduce((a,b)=>a+b,0) : forecastValues.reduce((a,b)=>a+b,0) * (12/forecastN);
  const trend = slope > 0 ? '📈 Growing' : slope < 0 ? '📉 Declining' : '➡️ Stable';

  // Platform breakdown
  const platformTotals = {};
  invoices.filter(inv => ['paid','partial'].includes(inv.status)).forEach(inv => {
    const p = inv.platform || 'other';
    platformTotals[p] = (platformTotals[p] || 0) + parseFloat(inv.amountInINR || inv.total || 0);
  });
  const topPlatforms = Object.entries(platformTotals).sort((a,b)=>b[1]-a[1]).slice(0,5);
  const totalRevenue = Object.values(platformTotals).reduce((a,b)=>a+b,0);

  const COLORS = ['#667eea','#10b981','#f59e0b','#f43f5e','#0ea5e9'];

  if (loading) return <div style={{ padding:60, textAlign:'center', color:'#94a3b8' }}>Loading revenue data…</div>;

  return (
    <div className="hr-page">
      <div style={{ marginBottom:28 }}>
        <h1 style={{ margin:0, fontSize:28, fontWeight:800 }}>Revenue Forecasting</h1>
        <p style={{ margin:'4px 0 0', color:'#94a3b8', fontSize:13 }}>AI-driven revenue projections based on historical invoice data</p>
      </div>

      {/* KPI Strip */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:16, marginBottom:28 }}>
        {[
          { label:'Avg Monthly Revenue', value:INR(avgMonthly), color:'#667eea', icon:'💰' },
          { label:'Next Month Forecast',  value:INR(nextMonth),  color:'#10b981', icon:'📅' },
          { label:'Next Quarter Forecast',value:INR(nextQuarter),color:'#f59e0b', icon:'📆' },
          { label:'Annual Forecast',      value:INR(nextYear),   color:'#0ea5e9', icon:'📊' },
          { label:'Revenue Trend',        value:trend,           color: slope>0?'#10b981':slope<0?'#ef4444':'#94a3b8', icon:'📈' },
        ].map(k => (
          <div key={k.label} className="card" style={{ padding:20 }}>
            <div style={{ fontSize:22, marginBottom:6 }}>{k.icon}</div>
            <div style={{ fontSize:20, fontWeight:800, color:k.color }}>{k.value}</div>
            <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="card" style={{ padding:24, marginBottom:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ margin:0, fontWeight:700 }}>Revenue Trend & Forecast</h3>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <span style={{ fontSize:12, color:'#64748b' }}>Forecast months:</span>
            {[3,6,12].map(n => (
              <button key={n} onClick={() => setForecastN(n)}
                style={{ padding:'5px 14px', borderRadius:8, border:'1.5px solid #e2e8f0', background: forecastN===n?'var(--pm-gradient)':'#fff', color: forecastN===n?'#fff':'#64748b', fontWeight:600, fontSize:12, cursor:'pointer' }}>
                {n}M
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={allLabels.map((label,i) => ({ label, actual: allActual[i], forecast: allForecast[i] }))} margin={{ top:10, right:10, left:10, bottom:0 }}>
            <defs>
              <linearGradient id="actualGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#667eea" stopOpacity={0.18}/>
                <stop offset="95%" stopColor="#667eea" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="forecastGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.18}/>
                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
            <XAxis dataKey="label" tick={{ fontSize:11, fill:'#94a3b8' }}/>
            <YAxis tickFormatter={v => v >= 100000 ? `₹${(v/100000).toFixed(1)}L` : `₹${(v/1000).toFixed(0)}k`} tick={{ fontSize:11, fill:'#94a3b8' }} width={70}/>
            <Tooltip content={<CustomTooltip/>}/>
            <Legend wrapperStyle={{ fontSize:12, paddingTop:8 }}/>
            <ReferenceLine x={monthlyRevenue[monthlyRevenue.length-1]?.label} stroke="#e2e8f0" strokeDasharray="4 4" label={{ value:'Now', fontSize:10, fill:'#94a3b8' }}/>
            <Area type="monotone" dataKey="actual" name="Actual Revenue" stroke="#667eea" fill="url(#actualGrad)" strokeWidth={2.5} dot={{ r:3, fill:'#667eea' }} connectNulls={false}/>
            <Area type="monotone" dataKey="forecast" name="Forecast" stroke="#10b981" fill="url(#forecastGrad)" strokeWidth={2.5} strokeDasharray="6 4" dot={{ r:3, fill:'#10b981' }} connectNulls={false}/>
          </ComposedChart>
        </ResponsiveContainer>
        <p style={{ margin:'12px 0 0', fontSize:12, color:'#94a3b8', textAlign:'center' }}>
          Forecast uses linear regression on the last 12 months of actual revenue. Actual values represent total INR revenue from paid/partial invoices.
        </p>
      </div>

      {/* Platform Breakdown */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        <div className="card" style={{ padding:24 }}>
          <h3 style={{ margin:'0 0 16px', fontWeight:700 }}>Revenue by Platform</h3>
          {topPlatforms.map(([platform, amt], i) => {
            const pct = totalRevenue ? (amt/totalRevenue)*100 : 0;
            return (
              <div key={platform} style={{ marginBottom:14 }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginBottom:5 }}>
                  <span style={{ fontWeight:600, textTransform:'capitalize' }}>{platform.replace('_',' ')}</span>
                  <span style={{ color:'#64748b' }}>{INR(amt)} ({pct.toFixed(1)}%)</span>
                </div>
                <div style={{ height:8, background:'#f1f5f9', borderRadius:4, overflow:'hidden' }}>
                  <div style={{ height:'100%', width:`${pct}%`, background:COLORS[i%COLORS.length], borderRadius:4, transition:'width .5s' }}/>
                </div>
              </div>
            );
          })}
          {topPlatforms.length === 0 && <p style={{ color:'#94a3b8', fontSize:13 }}>No paid invoices yet</p>}
        </div>

        <div className="card" style={{ padding:24 }}>
          <h3 style={{ margin:'0 0 16px', fontWeight:700 }}>Monthly Breakdown</h3>
          <div style={{ maxHeight:280, overflowY:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  <th style={{ padding:'8px 10px', textAlign:'left', color:'#94a3b8', fontSize:11, fontWeight:700, textTransform:'uppercase' }}>Month</th>
                  <th style={{ padding:'8px 10px', textAlign:'right', color:'#94a3b8', fontSize:11, fontWeight:700, textTransform:'uppercase' }}>Revenue</th>
                  <th style={{ padding:'8px 10px', textAlign:'right', color:'#94a3b8', fontSize:11, fontWeight:700, textTransform:'uppercase' }}>vs Avg</th>
                </tr>
              </thead>
              <tbody>
                {[...monthlyRevenue].reverse().map((m,i) => {
                  const diff = m.revenue - avgMonthly;
                  return (
                    <tr key={i} style={{ borderBottom:'1px solid #f8fafc' }}>
                      <td style={{ padding:'9px 10px', fontWeight:600 }}>{m.label}</td>
                      <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:700, color:'#0f172a' }}>{INR(m.revenue)}</td>
                      <td style={{ padding:'9px 10px', textAlign:'right', fontSize:12, color: diff>0?'#10b981':diff<0?'#ef4444':'#94a3b8', fontWeight:600 }}>
                        {diff>0?'+':''}{INR(diff)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
